import type { CoachJourneyInput } from '../../schema';
import { PHASE_1, RECORD_CARD, STORY_CARD } from './scripts';

/** The first half: the brand profile and its board, from the website to the saved profile card. */
export const brandProfileSteps: CoachJourneyInput['steps'] = [
  {
    id: 'open',
    display: 'hidden',
    title: 'Open a board.',
    body: 'Open a board you can edit.',
    on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'make-brand' }],
  },
  {
    id: 'make-brand',
    display: 'toast',
    title: 'Setting up your brand profile 🧬',
    body: 'Making a board just for it. One sec…',
    do: [
      {
        action: 'brand.create',
        params: { name: 'My brand' },
        bind: {
          brandId: 'brandId',
          brandBoardId: 'brandBoardId',
          businessGroupId: 'businessGroupId',
          voiceGroupId: 'voiceGroupId',
        },
        onError: 'brand-full',
      },
      { action: 'board.open', params: { board: 'brandBoardId' }, then: 'enter-brand-board' },
    ],
  },
  {
    // The one card: brand slots are full (a plan limit), which the person has to fix.
    id: 'brand-full',
    title: 'No room for another brand profile 👀',
    body: 'Your brand slots are full. Delete one in Settings → Brands, then come back here and try again.',
    action: { label: 'Try again', to: 'make-brand' },
  },
  {
    id: 'enter-brand-board',
    display: 'hidden',
    title: 'Opening your brand board.',
    body: 'Opening your brand board.',
    on: [
      {
        event: 'board.entered',
        matchAs: { boardId: 'brandBoardId' },
        bind: ['boardId'],
        to: 'start-chat',
      },
    ],
  },
  {
    id: 'start-chat',
    display: 'hidden',
    title: 'Opening Poppy.',
    body: 'Opening Poppy.',
    do: [
      // Both groups on screen first, so what Poppy adds is seen landing in them.
      { action: 'board.show', params: { nodes: ['businessGroupId', 'voiceGroupId'] } },
      {
        action: 'copilot.ask',
        params: {
          prompt: "Hey Poppy! Let's build my brand profile and voice 🌸",
          instructions: PHASE_1,
        },
      },
    ],
    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'interview' }],
  },
  {
    // The whole of phase 1 is the conversation. Poppy signals when the profile is confirmed.
    id: 'interview',
    // Back on the board after a reload or the start link: Poppy's panel opens again.
    resumeAt: 'reopen-interview',
    display: 'hidden',
    keepInView: ['businessGroupId', 'voiceGroupId'],
    title: 'Building your brand profile with Poppy.',
    body: 'Building your brand profile with Poppy.',
    on: [
      // A recording that lands before the record card (Poppy skipped its marker, or they
      // tapped the mic early) is the recording: hear it out instead of asking again.
      {
        event: 'node.added',
        match: ['boardId'],
        if: { nodeType: 'audioNode' },
        bindAs: { nodeId: 'recordingId' },
        to: 'place-recording',
      },
      { event: 'copilot.signal', match: ['boardId'], if: { signal: 'record' }, to: 'record' },
      { event: 'copilot.signal', match: ['boardId'], if: { signal: 'voice' }, to: 'voice' },
      // Poppy sometimes leaves the voice marker off the hand-off: the first thing she puts
      // in My Voice (their connected profile, a link) is Part B starting all the same.
      {
        event: 'node.added',
        match: ['boardId'],
        matchAs: { parentId: 'voiceGroupId' },
        to: 'voice',
      },
      // Poppy sometimes skips the voice marker when the handoff reply also used tools: the
      // story card must still come.
      { event: 'copilot.signal', match: ['boardId'], if: { signal: 'story' }, to: 'story' },
      {
        event: 'copilot.signal',
        match: ['boardId'],
        if: { signal: 'confirmed' },
        to: 'save-default',
      },
    ],
  },
  {
    // Part B is the conversation too; what lands now (a clip, a Loom) belongs in My Voice.
    id: 'voice',
    // Back on the board after a reload or the start link: Poppy's panel opens again.
    resumeAt: 'reopen-voice',
    display: 'hidden',
    keepInView: ['voiceGroupId', 'businessGroupId'],
    title: 'Building My Voice and Content with Poppy.',
    body: 'Building My Voice and Content with Poppy.',
    on: [
      {
        event: 'copilot.signal',
        match: ['boardId'],
        if: { signal: 'readback' },
        to: 'ask-readback',
      },
      // A recording that lands before the story card is the story: hear it out.
      {
        event: 'node.added',
        match: ['boardId'],
        if: { nodeType: 'audioNode' },
        bindAs: { nodeId: 'storyId' },
        to: 'place-story',
      },
      { event: 'copilot.signal', match: ['boardId'], if: { signal: 'story' }, to: 'story' },
      {
        event: 'copilot.signal',
        match: ['boardId'],
        if: { signal: 'confirmed' },
        to: 'save-default',
      },
    ],
  },
  {
    id: 'reopen-interview',
    display: 'hidden',
    title: 'Opening Poppy again.',
    body: 'Opening Poppy again.',
    // A reload starts a fresh chat: Poppy reads the board and carries on from there.
    do: { action: 'copilot.ask', params: { prompt: "I'm back 👋 Where were we?" } },
    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'interview' }],
  },
  {
    id: 'reopen-voice',
    display: 'hidden',
    title: 'Opening Poppy again.',
    body: 'Opening Poppy again.',
    // A reload starts a fresh chat: Poppy reads the board and carries on from there.
    do: { action: 'copilot.ask', params: { prompt: "I'm back 👋 Where were we?" } },
    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'voice' }],
  },
  {
    id: 'reopen-story',
    display: 'hidden',
    title: 'Opening Poppy again.',
    body: 'Opening Poppy again.',
    do: { action: 'copilot.open', then: 'story' },
  },
  {
    id: 'reopen-record',
    display: 'hidden',
    title: 'Opening Poppy again.',
    body: 'Opening Poppy again.',
    do: { action: 'copilot.open', then: 'record' },
  },
  {
    id: 'reopen-building',
    display: 'hidden',
    title: 'Opening Poppy again.',
    body: 'Opening Poppy again.',
    // A reload starts a fresh chat: Poppy reads the board and carries on from there.
    do: { action: 'copilot.ask', params: { prompt: "I'm back 👋 Where were we?" } },
    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'building' }],
  },
  {
    // Poppy wrapped up before reading the profile back: the person asks for the read-back,
    // so their yes builds the brand profile card.
    id: 'ask-readback',
    display: 'hidden',
    title: 'Asking for the read-back.',
    body: 'Asking for the read-back.',
    do: {
      action: 'copilot.ask',
      params: { prompt: 'Read my brand profile back to me so I can confirm it 🧬' },
    },
    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'voice' }],
  },
  {
    // The story: two minutes of them talking like they would to a friend.
    id: 'story',
    // Back on the board after a reload or the start link: Poppy's panel opens again.
    resumeAt: 'reopen-story',
    keepInView: ['voiceGroupId', 'businessGroupId'],
    title: 'Tap + and record your story 🎤',
    body: STORY_CARD,
    show: [{ anchor: 'copilot.attach', placement: 'left' }],
    action: { label: 'Skip for now', to: 'skip-story' },
    on: [
      {
        event: 'node.added',
        match: ['boardId'],
        if: { nodeType: 'audioNode' },
        bindAs: { nodeId: 'storyId' },
        to: 'place-story',
      },
    ],
  },
  {
    // The story belongs in My Voice, wherever it landed (the chat's mic drops it loose).
    // Already there: nothing to move, which is fine.
    id: 'place-story',
    display: 'hidden',
    keepInView: ['voiceGroupId', 'businessGroupId'],
    title: 'Putting the story in My Voice.',
    body: 'Putting the story in My Voice.',
    do: {
      action: 'board.move-to-group',
      params: { nodes: ['storyId'], group: 'voiceGroupId' },
      then: 'transcribing-story',
      onError: 'transcribing-story',
    },
  },
  {
    id: 'transcribing-story',
    display: 'toast',
    keepInView: ['voiceGroupId', 'businessGroupId'],
    title: 'Listening to your story 🎧',
    body: 'Writing it down. One sec…',
    waitFor: { nodes: ['storyId'], to: 'saved-story', atMost: 600 },
  },
  {
    id: 'saved-story',
    display: 'toast',
    keepInView: ['voiceGroupId', 'businessGroupId'],
    title: 'Listening to your story 🎧',
    body: 'Writing it down. One sec…',
    resumeAt: 'saved-story',
    do: {
      action: 'brand.await-text',
      params: { brand: 'brandId', node: 'storyId' },
      then: 'heard-story',
      onError: 'heard-story',
    },
  },
  {
    id: 'heard-story',
    display: 'hidden',
    title: 'Reading the story.',
    body: 'Reading the story.',
    do: { action: 'copilot.ask', params: { prompt: 'Done with my story 🎤' } },

    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'voice' }],
  },
  {
    id: 'skip-story',
    display: 'hidden',
    title: 'Skipping the story.',
    body: 'Skipping the story.',
    do: { action: 'copilot.ask', params: { prompt: 'Skip the story for now' } },

    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'voice' }],
  },
  {
    // The one card in the interview: how to record the answers in one take.
    id: 'record',
    // Back on the board after a reload or the start link: Poppy's panel opens again.
    resumeAt: 'reopen-record',
    keepInView: ['businessGroupId', 'voiceGroupId'],
    title: 'Tap + and just talk 🎤',
    body: RECORD_CARD,
    show: [{ anchor: 'copilot.attach', placement: 'left' }],
    action: { label: "I'd rather type", to: 'typing' },
    on: [
      {
        event: 'node.added',
        match: ['boardId'],
        if: { nodeType: 'audioNode' },
        bindAs: { nodeId: 'recordingId' },
        to: 'place-recording',
      },
    ],
  },
  {
    // The recording belongs in My Business, wherever it landed. Already there: fine.
    id: 'place-recording',
    display: 'hidden',
    keepInView: ['businessGroupId', 'voiceGroupId'],
    title: 'Putting the recording in My Business.',
    body: 'Putting the recording in My Business.',
    do: {
      action: 'board.move-to-group',
      params: { nodes: ['recordingId'], group: 'businessGroupId' },
      then: 'transcribing',
      onError: 'transcribing',
    },
  },
  {
    id: 'transcribing',
    display: 'toast',
    keepInView: ['businessGroupId', 'voiceGroupId'],
    title: 'Listening to your recording 🎧',
    body: 'Writing it down. One sec…',
    waitFor: { nodes: ['recordingId'], to: 'saved-recording', atMost: 600 },
  },
  {
    // The card shows live words first; Poppy reads the transcript the server saves after.
    id: 'saved-recording',
    display: 'toast',
    keepInView: ['businessGroupId', 'voiceGroupId'],
    title: 'Listening to your recording 🎧',
    body: 'Writing it down. One sec…',
    resumeAt: 'saved-recording',
    do: {
      action: 'brand.await-text',
      params: { brand: 'brandId', node: 'recordingId' },
      then: 'heard',
      onError: 'heard',
    },
  },
  {
    id: 'heard',
    display: 'hidden',
    title: 'Reading the recording.',
    body: 'Reading the recording.',
    do: { action: 'copilot.ask', params: { prompt: 'Done recording 🎤' } },

    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'interview' }],
  },
  {
    id: 'typing',
    display: 'hidden',
    title: 'Typing instead.',
    body: 'Typing instead.',
    do: { action: 'copilot.ask', params: { prompt: "I'd rather type ⌨️" } },

    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'interview' }],
  },
  {
    // The wrap on Phase 1: the two groups are wired into a Brand profile card on this very
    // board, so they see what they built become their profile; it is saved under Brand
    // profiles (this board is what opens there) and switched on for every chat.
    id: 'save-default',
    resumeAt: 'save-default',
    display: 'toast',
    title: 'Saving your brand profile 🧬',
    body: 'Switching it on for every chat.',
    do: [
      {
        action: 'board.add-brand-card',
        params: { brand: 'brandId', connect: ['businessGroupId', 'voiceGroupId'] },
        bind: { cardNodeId: 'brandCardId' },
      },
      { action: 'brand.set-default', params: { brand: 'brandId' } },
      {
        action: 'board.show',
        params: { nodes: ['businessGroupId', 'voiceGroupId', 'brandCardId'] },
      },
      { action: 'app.celebrate', then: 'brand-saved' },
    ],
  },
  {
    id: 'brand-saved',
    keepInView: ['businessGroupId', 'voiceGroupId', 'brandCardId'],
    // Beside the card, which stays bright while its profile fills in and glows.
    show: [{ node: 'brandCardId', placement: 'left' }],
    title: 'This is your brand profile 🧬',
    body: "Everything in My Business and My Voice and Content just became this card: your vibe, your tone, the words you actually use. It's switched on for every chat you open.\n\nFind it anytime under Settings → Brands, and add to it like a board: a note, a file, a clip.\n\nNext up: your first board.",
    action: { label: "Let's build my first board", to: 'summary' },
  },
  {
    id: 'summary',
    display: 'hidden',
    title: 'Reading your brand profile.',
    body: 'Reading your brand profile.',
    do: {
      action: 'brand.summary',
      params: { brand: 'brandId' },
      bind: { brandSummary: 'brandSummary' },
      then: 'make-first-board',
    },
  },
];
